import { Flex, Segmented, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { orDash } from '../../lib/format';
import { listUnits, type Unit } from './api';

const { Text } = Typography;

/** Stock aging is the number NKS cares about — days on the floor unsold. */
const AGING_WARN_DAYS = 60;
const AGING_BAD_DAYS = 120;

type View = 'available' | 'registration' | 'all';

export function UnitListPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('all');

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['units', view, search], (page) =>
    listUnits({
      ...page,
      q: search,
      ...(view === 'available' ? { available: true } : {}),
      ...(view === 'registration' ? { stockStatus: 'REGISTRATION' as const } : {}),
    }),
  );

  const ageTone = (days: number | null): 'danger' | 'warning' | undefined => {
    if (days === null) return undefined;
    if (days >= AGING_BAD_DAYS) return 'danger';
    return days >= AGING_WARN_DAYS ? 'warning' : undefined;
  };

  return (
    <Flex vertical gap={12}>
      <Segmented<View>
        value={view}
        onChange={(value) => {
          setView(value);
        }}
        size="large"
        block
        options={[
          { value: 'all', label: t('vehicles.viewAll') },
          { value: 'available', label: t('vehicles.viewAvailable') },
          { value: 'registration', label: t('vehicles.viewRegistration') },
        ]}
      />

      <ListPage<Unit>
        title={t('vehicles.title')}
        searchPlaceholder={t('vehicles.search')}
        onSearch={(value) => {
          setSearch(value);
        }}
        loading={isFetching}
        emptyDescription={search.length > 0 ? t('vehicles.noResults') : t('vehicles.empty')}
        columns={[
          {
            title: t('vehicles.fields.chassisNo'),
            dataIndex: 'chassisNo',
            width: 190,
            render: (value: string) => <Text className="tabular">{value}</Text>,
          },
          {
            title: t('sales.fields.model'),
            dataIndex: 'model',
            render: (value: string | null, row: Unit) => (
              <Flex vertical gap={2}>
                <Text>{orDash(value)}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {orDash(row.colour)}
                </Text>
              </Flex>
            ),
          },
          {
            title: t('vehicles.fields.status'),
            dataIndex: 'stockStatus',
            width: 165,
            render: (status: Unit['stockStatus']) => <Tag>{t(`vehicles.status.${status}`)}</Tag>,
          },
          {
            title: t('vehicles.fields.location'),
            dataIndex: 'location',
            width: 165,
            render: (value: string | null) => orDash(value),
          },
          {
            title: t('vehicles.fields.allocatedTo'),
            dataIndex: 'allocatedCustomerName',
            width: 180,
            render: (value: string | null) => orDash(value),
          },
          {
            title: t('vehicles.fields.age'),
            dataIndex: 'ageDays',
            width: 130,
            align: 'right' as const,
            // Only counted while a bike is still unsold — the API stops the
            // clock, so a delivered unit does not accrue a worse number forever.
            render: (days: number | null) =>
              days === null ? (
                '—'
              ) : (
                <Text type={ageTone(days)} strong={ageTone(days) !== undefined}>
                  {t('vehicles.daysOnFloor', { count: days })}
                </Text>
              ),
          },
        ]}
        rows={rows}
        hasMore={hasMore}
        onLoadMore={loadMore}
        rowKey={(row) => row.uid}
        onRowClick={(row) => {
          void navigate(`/units/${row.uid}`);
        }}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong className="tabular">
                {row.chassisNo}
              </Text>
              <Tag style={{ marginInlineEnd: 0 }}>{t(`vehicles.status.${row.stockStatus}`)}</Tag>
            </Flex>
            <Flex justify="space-between" gap={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {orDash(row.model)}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {orDash(row.location)}
              </Text>
            </Flex>
          </Flex>
        )}
      />
    </Flex>
  );
}
